import { DataLayer, FormLayerObject, PageLayerObject, AcquisitionLayerObject, InternalLayerObject, CtaLayerObject } from '@models/DataLayerR3.model';
import { Helpers } from './helpers';
declare const window: any;

// `window._satellite` is injected by the Adobe Launch (DTM) tag, which is loaded via a
// <script> tag and can still be initialising for a second or two after our React app has
// already rendered. Every track* method below checks `if (window._satellite)` first; when
// it isn't ready yet, `retry()` schedules another attempt shortly after instead of firing
// nothing at all.
export enum TrackType {
  PAGE_LOAD = "track_page_load",
  FORM_COMPLETE = "track_form_complete",
  FORM_ERROR = "track_form_error",
  FORM_START = "track_form_start",
  CTA_CLICK = "track_cta_click",
  PURCHASE_SUCCESS = "track_successful_purchase"
}

// QB-AA-Tracking fix: shared name for the whole MLTC_R3 journey, so every step pushes the
// exact same `journeyName` into the data layer. Each page is then responsible for its own
// `stepName`/`stepNumber` (see the `trackOnLoad` in each page).
export const MLTC_R3_JOURNEY_NAME = "CareShield";

const SATELLITE_WARMUP_MS = 800;
let satelliteFirstSeenAt: number | null = null;
function isSatelliteReady(): boolean {
  if (!window._satellite) return false;
  if (satelliteFirstSeenAt === null) {
    satelliteFirstSeenAt = Date.now();
  }
  return Date.now() - satelliteFirstSeenAt >= SATELLITE_WARMUP_MS;
}

export class GAAviva {
  public digitalData: DataLayer = new DataLayer({});

  private static instance: GAAviva;
  private failed: any;
  private constructor(initial: any) {
    if (initial) {
      console.log("initial",initial);
      this.digitalData = new DataLayer(JSON.parse(initial));
    } else {
      const page = new PageLayerObject({});
      const form = new FormLayerObject({});
      const acquisition = new AcquisitionLayerObject({});
      const internal = new InternalLayerObject({});
      const cta = new CtaLayerObject({});
      this.digitalData = new DataLayer({ acquisition, page, form, cta, internal });
    }
  }

  public static getInstance(): GAAviva {
      if (!GAAviva.instance) {
        const now = new Date().getTime();
        const initial = sessionStorage.getItem('aviva_ga_cached');
        const persisExpiredTime = sessionStorage.getItem('persist:expired') || 0;
        if (now > Number(persisExpiredTime) || !persisExpiredTime) {
          GAAviva.instance = new GAAviva(null);
        } else {
          GAAviva.instance = new GAAviva(initial);
        }

      }
      console.log("<----GAAviva.instance---->",GAAviva.instance);
      return GAAviva.instance;

  }

  public trackFormCompleted(data: object): void {
    Object.assign(this.digitalData, data);
    const type = TrackType.FORM_COMPLETE;
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
        this.failed = null;
        console.debug({ type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      } catch (e) {
        this.retry(() => this.trackFormCompleted(data));
      }
    } else {
      console.error('AA script not loaded', { type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      this.retry(() => this.trackFormCompleted(data));
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));

  }

  public trackFormFailed(data: object): void {
    Object.assign(this.digitalData, data);
    const type = TrackType.FORM_ERROR;
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
        this.failed = null;
        console.debug({ type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      } catch (e) {
        this.failed = type;
        this.retry(() => { this.recallFailed(); });
      }
    } else {
      console.debug('AA script not loaded', { type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      this.retry(() => { this.recallFailed(); });
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));

  }

  // Fires the "track_page_load" AA event, i.e. a pageview. Every MLTC_R3 step calls this
  // once when it mounts. `data` is a partial `DataLayer` (e.g. { form, page }) that gets
  // shallow-merged onto the shared `digitalData` object; callers should include
  // `page: { journeyName, stepName, stepNumber }` so Adobe can tell this step apart from
  // the others in the journey.
  public trackPageLoad(data: object): void {
    const navs = Helpers.getNavLevels();
    Object.assign(this.digitalData, data);
    const { page } = this.digitalData;
    Object.assign(this.digitalData, { page: { ...page, ...navs } });
    const type = TrackType.PAGE_LOAD;
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
        this.failed = null;
        console.debug({ type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
        } catch (e) {
        this.retry(() => this.trackPageLoad(data));
      }
    } else {
      this.failed = type;
      console.debug('R3 AA script not loaded', { type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      this.retry(() => this.trackPageLoad(data));
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));
  }
  public trackPageStart(): void {
    const type = TrackType.FORM_START;
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
        this.failed = null;
        console.debug({ type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      } catch (e) {
        this.retry(() => this.trackPageStart());
      }
    } else {
      this.failed = type;
      console.debug('R3 AA script not loaded', { type: type, digitalData: this.digitalData, form: this.digitalData.form.name });
      this.retry(() => this.trackPageStart());
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));

  }

  public trackCTAClick(data: object): void {
    const type = TrackType.CTA_CLICK;
    Object.assign(this.digitalData, data);
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
      } catch (e) {
        this.retry(() => this.trackCTAClick(data));
      }
    } else {
      this.failed = type;
      console.debug('R3 trackCTAClick AA script not loaded ', { type: type, digitalData: this.digitalData, form: this.digitalData.cta });
      this.retry(() => this.trackCTAClick(data));
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));
  }

  public trackPurchaseCompleted(data: object): void {
    const type = TrackType.PURCHASE_SUCCESS;
    if (isSatelliteReady()) {
      try {
        window.digitalData = this.digitalData;
        window._satellite.track(type);
      } catch (e) {
        this.retry(() => this.trackPurchaseCompleted(data));
      }
    } else {
      this.failed = type;
      console.debug('R3 trackPurchaseCompleted AA script not loaded ', { type: type, digitalData: this.digitalData, form: this.digitalData.cta });
      this.retry(() => this.trackPurchaseCompleted(data));
    }
    sessionStorage.setItem('aviva_ga_cached', JSON.stringify(this.digitalData));
  }

  // QB-AA-Tracking fix: this used to wait a flat 10 seconds before retrying, which meant
  // a pageview could sit unsent for up to 10s while `_satellite` finished loading - long
  // enough that the user could navigate away or click through before the event ever fired,
  // so it was silently lost. Polling every 300ms instead means we fire almost immediately
  // once `_satellite` becomes available (it normally loads within 1-2s), while still giving
  // it time to load on slower connections.
  public retry(callback: any) {
    setTimeout(callback , 300);
  }

  public recallFailed(): void {
    if (this.failed && isSatelliteReady()) {
      window.digitalData = this.digitalData;
      window._satellite.track(this.failed);
      console.debug('Recall', { type: this.failed, digitalData: this.digitalData, form: this.digitalData.form.name });
      this.failed = null;
    }
  }
}